import * as React from "react";

import { IterUtils } from "../../../../../../Common/IterableUtils";
import { useLocalStorage } from "../../../../../../Common/Hooks/UseLocalStorage";
import { useWindowEventListener } from "../../../Commons/DOM/UseWindowEventListener";
import { useKeyboardShortcut } from "../../../Commons/Keyboard/UseKeyboardShortcut";
import { emptyRef } from "../../../TypingUtils";

import { FixedRulerGridRoot, RulerGridHorizontalLine, RulerGridVerticalLine } from "./RulerGrid.styles";

interface RulerGridProps {
    children: React.ReactNode;
}

enum RulerGridMode {
    None = "None",
    AtCursor = "AtCursor",
    Screen = "Screen",
}

export function RulerGrid(props: RulerGridProps): JSX.Element {
    const [mode, setMode] = useLocalStorage("Candy:RulerGrid.Mode", RulerGridMode.None);

    const root = React.useRef<HTMLDivElement>(emptyRef);
    const handleScroll = React.useCallback(() => {
        if (mode === RulerGridMode.None) {
            return;
        }
        const boxSize = 16;
        const preScrollPosition = Math.round(window.scrollY / boxSize) * boxSize;
        const newScrollPosition =
            window.scrollY - preScrollPosition < boxSize / 2 ? preScrollPosition : preScrollPosition + boxSize;
        window.scroll({ top: newScrollPosition });
    }, [mode]);

    const handleMousemove = (e: MouseEvent) => {
        if (mode === RulerGridMode.AtCursor) {
            if (root.current) {
                root.current.style[
                    // @ts-ignore
                    "-webkit-mask-image"
                ] =
                    `-webkit-gradient(radial, ${e.x} ${e.y}, 0, ${e.x} ${e.y}, 400, color-stop(0, rgba(0,0,0,1)), color-stop(1, rgba(0,0,0,0)))`;
            }
        }
    };

    useWindowEventListener("scroll", handleScroll);
    useWindowEventListener("mousemove", handleMousemove);

    useKeyboardShortcut(
        "Ctrl+Alt+G",
        React.useCallback(() => {
            if (mode === RulerGridMode.None) {
                setMode(RulerGridMode.AtCursor);
                if (root.current) {
                    root.current.style[
                        // @ts-ignore
                        "-webkit-mask-image"
                    ] =
                        `-webkit-gradient(radial, 50% 50%, 0, 50% 50%, 400, color-stop(0, rgba(0,0,0,1)), color-stop(1, rgba(0,0,0,0)))`;
                }
            } else if (mode === RulerGridMode.AtCursor) {
                setMode(RulerGridMode.Screen);
                if (root.current) {
                    // @ts-ignore
                    root.current.style["-webkit-mask-image"] = "";
                }
            } else if (mode === RulerGridMode.Screen) {
                setMode(RulerGridMode.None);
            } else {
                setMode(RulerGridMode.Screen);
            }
        }, [mode])
    );

    return (
        <>
            {props.children}
            {mode != RulerGridMode.None && (
                <FixedRulerGridRoot ref={root}>
                    {[...IterUtils.range(500)].map(offset => (
                        <RulerGridHorizontalLine offset={offset} key={offset} opacity={offset % 4 === 0 ? 0.1 : 0.05} />
                    ))}
                    {[...IterUtils.range(500)].map(offset => (
                        <RulerGridVerticalLine offset={offset} key={offset} opacity={offset % 4 === 0 ? 0.1 : 0.05} />
                    ))}
                </FixedRulerGridRoot>
            )}
        </>
    );
}
